import { REGIONS } from 'v2/configs/constants'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { PlanType } from 'v2/types'
import type { ROrganization } from 'v2/types/api/settings'
import { getLocalAuth } from 'v2/utils'

export interface ChannelLocks {
  /** `/whatsapp` renders the upgrade CTA instead of the real UI. */
  isWhatsappLocked: boolean
  /** `/verify` and `/verify/app/:app_uuid` render the upgrade CTA instead of the real UI. */
  isVerifyLocked: boolean
  /**
   * Verify is not sold in India — the India plans deliberately omit it (see
   * `plans.config.ts`, pinned by `india-plans.test.ts`). So in that region the product
   * does not exist rather than being locked: every surface HIDES it, no upgrade CTA,
   * because there is nothing to upgrade to. Unlike the locks this is synchronous
   * (localStorage), so it has no loading window.
   */
  isVerifyAvailable: boolean
}

/** Only the keys the locks read, so tests don't have to build a whole ROrganization. */
type ChannelFlags = Pick<ROrganization, 'whatsapp_disabled' | 'verify_disabled'>

/**
 * Enterprise always gets WhatsApp/Verify; below Enterprise the `*_disabled` org flag decides.
 * Not `isEnterpriseLocked` on purpose — its org-age grandfathering must not get a second vote
 * over an explicitly set flag. `=== true` because the value comes from an untyped upstream
 * blob. Unresolved profile reads as unlocked, so callers acting on a lock wait on
 * `isChannelLocksLoaded`.
 */
export const deriveChannelLocks = (
  plan: PlanType | undefined,
  org: ChannelFlags | undefined,
  region?: string,
): ChannelLocks => {
  const isEnterprisePlan = plan === PlanType.ENTERPRISE
  return {
    isWhatsappLocked: !isEnterprisePlan && org?.whatsapp_disabled === true,
    isVerifyLocked: !isEnterprisePlan && org?.verify_disabled === true,
    // Enumerated, not a default-deny: an unknown region keeps Verify rather than
    // silently hiding a paid product the account may already be using.
    isVerifyAvailable: region !== REGIONS.IN,
  }
}

/** Single source for both locks — pages and useRoutes share it so nav and page can't disagree. */
export const useChannelLocks = (): ChannelLocks & { isChannelLocksLoaded: boolean } => {
  const { data: profile, isFetched } = useProfileQuery()
  return {
    ...deriveChannelLocks(profile?.plan, profile?.organizations?.[0], getLocalAuth()?.region),
    isChannelLocksLoaded: isFetched,
  }
}
